        // Enhanced Authentication System for Signup
        class EnhancedSignupSystem {
            constructor() {
                this.formProgress = 0;
                this.validationRules = {
                    name: /^[a-zA-Z\s]{2,30}$/,
                    email: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/,
                    phone: /^[6-9]\d{9}$/,
                    password: {
                        minLength: 8,
                        hasUpper: /[A-Z]/,
                        hasLower: /[a-z]/,
                        hasNumber: /\d/,
                        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/
                    }
                };

                this.initializeValidation();
                this.initializeProgressTracking();
            }

            saveCurrentUser(user) {
                const userData = {
                    ...user,
                    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
                };
                localStorage.setItem('burgerPrimeCurrentUser', JSON.stringify(userData));
            }

            async signup(userData) {
                try {
                    // Wait for Supabase to be ready
                    const api = await window.SupabaseReady;
                    if (!api) {
                        throw new Error('Authentication service is not available. Please try again later.');
                    }

                    const result = await api.auth.signUp({
                        email: userData.email,
                        password: userData.password,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        phone: userData.phone
                    });

                    if (result.success) {
                        // Auto login after signup - save user info
                        const loginUser = {
                            id: result.user.id,
                            name: `${userData.firstName} ${userData.lastName}`,
                            email: result.user.email,
                            phone: userData.phone,
                            loginTime: new Date().toISOString()
                        };

                        this.saveCurrentUser(loginUser);

                        // Log signup event
                        this.logEvent('user_signup', {
                            userId: result.user.id,
                            method: 'email',
                            timestamp: new Date().toISOString()
                        });

                        return { success: true, user: loginUser };
                    } else {
                        return {
                            success: false,
                            message: result.error || 'Registration failed. Please try again.'
                        };
                    }
                } catch (error) {
                    console.error('Signup error:', error);

                    return {
                        success: false,
                        message: error.message || 'Registration failed. Please try again.'
                    };
                }
            }

            generateUserId() {
                return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            }

            generateSessionId() {
                return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            }

            logEvent(eventType, data) {
                const events = JSON.parse(localStorage.getItem('burgerPrime_events') || '[]');
                events.push({
                    event: eventType,
                    data,
                    timestamp: new Date().toISOString()
                });
                
                // Keep only last 100 events
                if (events.length > 100) {
                    events.splice(0, events.length - 100);
                }
                
                localStorage.setItem('burgerPrime_events', JSON.stringify(events));
            }

            initializeValidation() {
                const inputs = document.querySelectorAll('[data-validation]');
                inputs.forEach(input => {
                    input.addEventListener('blur', () => this.validateField(input));
                    input.addEventListener('input', () => {
                        this.clearValidation(input);
                        if (input.id === 'signupPassword') {
                            this.updatePasswordStrength(input.value);
                        }
                        this.updateProgress();
                    });
                });

                // Special handling for confirm password
                const confirmPassword = document.getElementById('confirmPassword');
                const password = document.getElementById('signupPassword');
                
                confirmPassword.addEventListener('input', () => {
                    if (confirmPassword.value.length > 0) {
                        this.validateConfirmPassword();
                    }
                });

                password.addEventListener('input', () => {
                    if (confirmPassword.value.length > 0) {
                        this.validateConfirmPassword();
                    }
                });
            }

            initializeProgressTracking() {
                const inputs = document.querySelectorAll('.form-input');
                inputs.forEach(input => {
                    input.addEventListener('input', () => this.updateProgress());
                });
                
                document.getElementById('agreeTerms').addEventListener('change', () => this.updateProgress());
            }

            updateProgress() {
                const fields = ['firstName', 'lastName', 'signupEmail', 'signupPhone', 'signupPassword', 'confirmPassword'];
                const completedFields = fields.filter(fieldId => {
                    const field = document.getElementById(fieldId);
                    return field && field.value.trim().length > 0 && !field.classList.contains('invalid');
                }).length;

                const termsChecked = document.getElementById('agreeTerms').checked;
                const totalSteps = fields.length + (termsChecked ? 1 : 0);
                const progress = Math.floor((completedFields / fields.length) * 3);

                // Update progress steps
                for (let i = 1; i <= 3; i++) {
                    const step = document.getElementById(`step${i}`);
                    const line = document.getElementById(`line${i}`);
                    
                    if (i <= progress) {
                        step.classList.add('completed');
                        step.classList.remove('active');
                        if (line) line.classList.add('completed');
                    } else if (i === progress + 1) {
                        step.classList.add('active');
                        step.classList.remove('completed');
                    } else {
                        step.classList.remove('active', 'completed');
                        if (line) line.classList.remove('completed');
                    }
                }
            }

            validateField(input) {
                const validationType = input.dataset.validation;
                const value = input.value.trim();
                let isValid = false;
                let message = '';

                switch(validationType) {
                    case 'name':
                        isValid = this.validationRules.name.test(value);
                        message = isValid ? 'Valid name' : 'Name must be 2-30 characters, letters only';
                        break;
                    
                    case 'email':
                        // Normalize to lowercase and trim
                        input.value = value.toLowerCase().trim();
                        const email = input.value;

                        // Basic regex validation
                        isValid = this.validationRules.email.test(email);

                        // Additional domain validation checks
                        const hasConsecutiveDots = /\.\./.test(email);
                        const hasValidDomain = !hasConsecutiveDots &&
                                              !email.endsWith('.') &&
                                              !email.includes('@.') &&
                                              !email.includes('.@');

                        const finalValid = isValid && hasValidDomain;
                        isValid = finalValid;

                        if (isValid) {
                            message = 'Valid email format ✓';
                        } else {
                            message = 'Please enter a valid email address (e.g., user@gmail.com)';
                        }
                        break;

                    case 'phone':
                        const cleanPhone = value.replace(/\D/g, '');
                        isValid = this.validationRules.phone.test(cleanPhone);
                        if (isValid) {
                            message = 'Valid phone number';
                        } else {
                            message = 'Please enter a valid 10-digit phone number starting with 6-9';
                        }
                        break;
                    
                    case 'password':
                        const passwordValidation = this.validatePassword(value);
                        isValid = passwordValidation.isValid;
                        message = passwordValidation.message;
                        break;
                    
                    case 'confirmPassword':
                        isValid = this.validateConfirmPassword();
                        return isValid; // validateConfirmPassword handles its own messaging
                }

                this.showValidationResult(input, isValid, message);
                return isValid;
            }

            validatePassword(password) {
                const rules = this.validationRules.password;
                const requirements = {
                    length: password.length >= rules.minLength,
                    upper: rules.hasUpper.test(password),
                    lower: rules.hasLower.test(password),
                    number: rules.hasNumber.test(password),
                    special: rules.hasSpecial.test(password)
                };

                // Update requirement indicators
                Object.keys(requirements).forEach(req => {
                    const element = document.getElementById(`req-${req === 'length' ? 'length' : req}`);
                    const icon = element.querySelector('.requirement-icon');
                    
                    if (requirements[req]) {
                        element.classList.add('met');
                        icon.textContent = '✓';
                    } else {
                        element.classList.remove('met');
                        icon.textContent = '○';
                    }
                });

                const metRequirements = Object.values(requirements).filter(Boolean).length;
                const isValid = metRequirements >= 4; // At least 4 out of 5 requirements

                let message = '';
                if (metRequirements < 2) {
                    message = 'Password is too weak';
                } else if (metRequirements < 4) {
                    message = 'Password could be stronger';
                } else {
                    message = 'Strong password!';
                }

                return { isValid, message, strength: metRequirements };
            }

            updatePasswordStrength(password) {
                const validation = this.validatePassword(password);
                const strengthFill = document.getElementById('strengthFill');
                const strengthText = document.getElementById('strengthText');

                if (password.length === 0) {
                    strengthFill.className = 'strength-fill';
                    strengthText.textContent = 'Password strength';
                    return;
                }

                if (validation.strength <= 2) {
                    strengthFill.className = 'strength-fill weak';
                    strengthText.textContent = 'Weak';
                } else if (validation.strength <= 3) {
                    strengthFill.className = 'strength-fill medium';
                    strengthText.textContent = 'Medium';
                } else {
                    strengthFill.className = 'strength-fill strong';
                    strengthText.textContent = 'Strong';
                }
            }

            validateConfirmPassword() {
                const password = document.getElementById('signupPassword').value;
                const confirmPassword = document.getElementById('confirmPassword').value;
                const errorElement = document.getElementById('confirmPasswordError');
                const input = document.getElementById('confirmPassword');

                if (!confirmPassword) {
                    this.showValidationResult(input, false, 'Please confirm your password');
                    return false;
                } else if (password !== confirmPassword) {
                    this.showValidationResult(input, false, 'Passwords do not match');
                    return false;
                } else {
                    this.showValidationResult(input, true, 'Passwords match!');
                    return true;
                }
            }

            showValidationResult(input, isValid, message) {
                const errorElement = document.getElementById(input.getAttribute('aria-describedby'));
                
                if (isValid) {
                    input.classList.remove('invalid');
                    input.classList.add('valid');
                    errorElement.textContent = message;
                    errorElement.className = 'validation-message success show';
                } else {
                    input.classList.remove('valid');
                    input.classList.add('invalid');
                    errorElement.textContent = message;
                    errorElement.className = 'validation-message error show';
                }
            }

            clearValidation(input) {
                const errorElement = document.getElementById(input.getAttribute('aria-describedby'));
                input.classList.remove('valid', 'invalid');
                if (errorElement) {
                    errorElement.classList.remove('show');
                }
            }

            validateForm() {
                const fields = [
                    document.getElementById('firstName'),
                    document.getElementById('lastName'),
                    document.getElementById('signupEmail'),
                    document.getElementById('signupPhone'),
                    document.getElementById('signupPassword'),
                    document.getElementById('confirmPassword')
                ];

                let isFormValid = true;
                fields.forEach(field => {
                    const isFieldValid = this.validateField(field);
                    if (!isFieldValid) isFormValid = false;
                });

                // Check terms agreement
                const agreeTerms = document.getElementById('agreeTerms');
                if (!agreeTerms.checked) {
                    showNotification('Please agree to the Terms of Service and Privacy Policy', 'error');
                    isFormValid = false;
                }

                return isFormValid;
            }
        }

        // Signup system instance — created in DOMContentLoaded
        let signupSystem;

        // Enhanced signup form handler
        async function handleSignup(event) {
            event.preventDefault();
            
            // Validate form
            if (!signupSystem.validateForm()) {
                showNotification('Please fix the errors above', 'error');
                return;
            }

            const userData = {
                firstName: document.getElementById('firstName').value.trim(),
                lastName: document.getElementById('lastName').value.trim(),
                email: document.getElementById('signupEmail').value.trim(),
                phone: document.getElementById('signupPhone').value.replace(/\D/g, ''),
                password: document.getElementById('signupPassword').value
            };

            const signupBtn = document.getElementById('signupBtnText');
            const originalText = signupBtn.textContent;
            signupBtn.innerHTML = '<span class="loading"></span>Creating your account...';
            document.getElementById('signupBtn').disabled = true;
            
            try {
                const result = await signupSystem.signup(userData);
                
                if (result.success) {
                    showSuccessMessage(result.user);
                } else {
                    showNotification(result.message, 'error');
                }
            } catch (error) {
                console.error('Signup error:', error);
                showNotification('Account creation failed. Please try again.', 'error');
            } finally {
                if (!document.getElementById('signupBtn').innerHTML.includes('✅')) {
                    signupBtn.textContent = originalText;
                    document.getElementById('signupBtn').disabled = false;
                }
            }
        }

        // Success message with animation
        function showSuccessMessage(user) {
            const container = document.querySelector('.auth-container');
            container.innerHTML = `
                <div style="text-align: center; padding: 40px 20px;">
                    <svg class="success-checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                        <circle class="success-checkmark__circle" cx="26" cy="26" r="25" fill="none"/>
                        <path class="success-checkmark__check" fill="none" d="m14.1 27.2l7.1 7.2 16.7-16.8"/>
                    </svg>
                    <h2 style="color: var(--primary-crimson); margin-bottom: 15px; font-size: 2rem;">Welcome to Burger Prime!</h2>
                    <p style="color: var(--text-light); margin-bottom: 25px; font-size: 1.1rem;">
                        Hi <span id="welcomeUserName"></span>! Your account has been created successfully.
                    </p>
                    <div style="background: #f8f9fa; padding: 20px; border-radius: 12px; margin: 20px 0; text-align: left;">
                        <h3 style="color: var(--primary-crimson); margin-bottom: 15px; font-size: 1.2rem;">🎁 Welcome Bonus</h3>
                        <ul style="color: var(--text-dark); line-height: 1.8; padding-left: 20px;">
                            <li>🍔 20% off your first order</li>
                            <li>🎯 Free delivery on orders over ₹299</li>
                            <li>⭐ Early access to new menu items</li>
                            <li>📧 Exclusive deals via email</li>
                        </ul>
                    </div>
                    <p style="color: var(--text-light); margin-bottom: 30px; font-size: 0.9rem;">
                        You will be redirected to the main page in a few seconds...
                    </p>
                    <a href="index.html" class="auth-submit" style="display: inline-block; text-decoration: none; width: auto; padding: 15px 30px; margin: 0 10px;">
                        🍔 Start Ordering
                    </a>
                    <a href="login.html" class="auth-submit" style="display: inline-block; text-decoration: none; width: auto; padding: 15px 30px; margin: 0 10px; background: linear-gradient(135deg, #ccc, #999);">
                        Sign In Instead
                    </a>
                </div>
            `;

            // Set user name safely via textContent to prevent XSS
            const nameEl = document.getElementById('welcomeUserName');
            if (nameEl) nameEl.textContent = user.name;

            // Send welcome email (simulation)
            setTimeout(() => {
                showNotification('Welcome email sent! Check your inbox.', 'success');
            }, 1000);

            // Auto redirect after 8 seconds
            setTimeout(() => {
                window.location.href = 'index.html';
            }, 8000);
        }

        // Enhanced social signup functions with Supabase OAuth
        async function signupWithGoogle() {
            try {
                const api = await window.SupabaseReady;
                if (!api) {
                    showNotification('Authentication service not available. Please try again later.', 'error');
                    return;
                }

                showNotification('Redirecting to Google...', 'info');

                const result = await api.auth.signInWithOAuth({
                    provider: 'google',
                    redirectTo: `${window.location.origin}/index.html`
                });

                if (!result.success) {
                    throw new Error(result.error);
                }
            } catch (error) {
                console.error('Google signup error:', error);
                showNotification('Google signup failed. Please try again.', 'error');
            }
        }



        // Enhanced notification system
        function showNotification(message, type = 'info') {
            // Remove existing notifications
            const existingNotifications = document.querySelectorAll('.notification');
            existingNotifications.forEach(notification => notification.remove());
            
            const notification = document.createElement('div');
            notification.className = `notification ${type}`;
            const iconSpan = document.createElement('span');
            iconSpan.textContent = getNotificationIcon(type);
            const msgSpan = document.createElement('span');
            msgSpan.textContent = message;
            const wrapper = document.createElement('div');
            wrapper.style.cssText = 'display: flex; align-items: center; gap: 10px;';
            wrapper.appendChild(iconSpan);
            wrapper.appendChild(msgSpan);
            notification.appendChild(wrapper);
            
            document.body.appendChild(notification);
            
            // Auto remove after 5 seconds
            setTimeout(() => {
                notification.style.animation = 'slideOutRight 0.3s ease-in';
                setTimeout(() => notification.remove(), 300);
            }, 5000);
        }

        function getNotificationIcon(type) {
            const icons = {
                success: '✅',
                error: '❌',
                warning: '⚠️',
                info: 'ℹ️'
            };
            return icons[type] || 'ℹ️';
        }

        // Enhanced initialization
        document.addEventListener('DOMContentLoaded', function() {
            // Initialize signup system now that DOM is ready
            signupSystem = new EnhancedSignupSystem();

            // Wire up form submission
            const signupForm = document.getElementById('signupForm');
            if (signupForm) {
                signupForm.addEventListener('submit', handleSignup);
            }

            // Wire up Google signup button
            const googleBtn = document.getElementById('googleSignupBtn');
            if (googleBtn) {
                googleBtn.addEventListener('click', signupWithGoogle);
            }

            // Auto-focus first input
            const firstInput = document.getElementById('firstName');
            if (firstInput) firstInput.focus();
            
            // Check if user is already logged in
            const currentUser = localStorage.getItem('burgerPrimeCurrentUser');
            if (currentUser) {
                try {
                    const userData = JSON.parse(currentUser);
                    const isValid = !userData.expiresAt || new Date(userData.expiresAt) > new Date();
                    if (isValid) {
                        showNotification('You are already logged in. Redirecting...', 'info');
                        setTimeout(() => {
                            window.location.href = 'index.html';
                        }, 1500);
                    } else {
                        localStorage.removeItem('burgerPrimeCurrentUser');
                    }
                } catch (e) {
                    localStorage.removeItem('burgerPrimeCurrentUser');
                }
            }

            // Add input event listeners for enhanced UX
            const inputs = document.querySelectorAll('.form-input');
            inputs.forEach(input => {
                input.addEventListener('input', function() {
                    if (this.value.length > 0) {
                        this.classList.add('has-content');
                    } else {
                        this.classList.remove('has-content');
                    }
                });

                // Format phone number as user types
                if (input.id === 'signupPhone') {
                    input.addEventListener('input', function() {
                        let value = this.value.replace(/\D/g, '');
                        if (value.length > 10) {
                            value = value.substring(0, 10);
                        }
                        this.value = value;
                    });
                }

                // Auto-convert email to lowercase as user types
                if (input.id === 'signupEmail') {
                    input.addEventListener('input', function() {
                        this.value = this.value.toLowerCase();
                    });
                }
            });

            // Add keyboard shortcuts
            document.addEventListener('keydown', function(e) {
                // Submit on Ctrl+Enter
                if (e.ctrlKey && e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('signupForm').dispatchEvent(new Event('submit'));
                }
                
                // Clear form on Ctrl+R (prevent default refresh)
                if (e.ctrlKey && e.key === 'r') {
                    e.preventDefault();
                    if (confirm('Clear form data?')) {
                        document.getElementById('signupForm').reset();
                        signupSystem.updateProgress();
                        location.reload();
                    }
                }
            });

            // Initialize progress
            signupSystem.updateProgress();
        });

        // Enhanced error handling
        window.addEventListener('error', function(e) {
            console.error('Unexpected error:', e.error);
            showNotification('An unexpected error occurred. Please refresh the page.', 'error');
        });

        // Network status monitoring
        window.addEventListener('online', function() {
            showNotification('Connection restored', 'success');
        });

        window.addEventListener('offline', function() {
            showNotification('You are offline. Some features may not work.', 'warning');
        });
